import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Dialog,
  Field,
  Grid,
  Inline,
  MoneyField,
  Panel,
  SelectField,
  Spinner,
  Stack,
  StatusBadge,
  Switch,
  TextAreaField,
  useToast
} from "@leylion/design-system";
import {
  discardSettingsDraft,
  getSettingsSection,
  publishSettings,
  saveSettingsDraft
} from "../api/client";
import type {
  SettingsFieldDefinition,
  SettingsScopeType,
  SettingsSectionDefinition,
  SettingsSectionState
} from "../api/settings-types";
import { deletePath, formatDate, getPath, hasPath, setPath } from "./settings-utils";

type Language = "ar" | "en";

export function SettingsSectionEditor({
  section,
  scopeType,
  scopeId,
  language,
  currency,
  onStateChanged
}: {
  section: SettingsSectionDefinition;
  scopeType: SettingsScopeType;
  scopeId: string;
  language: Language;
  currency: string;
  onStateChanged?: () => void;
}) {
  const toast = useToast();
  const [state, setState] = useState<SettingsSectionState | null>(null);
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [dirty, setDirty] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const ar = language === "ar";
  const title = ar ? section.titleAr : section.titleEn;
  const description = ar ? section.descriptionAr : section.descriptionEn;

  const load = async () => {
    setBusy(true);
    setError("");
    try {
      const result = await getSettingsSection(section.key, scopeType, scopeId);
      setState(result);
      setValues(structuredClone(result.draftValues ?? result.publishedValues ?? {}));
      setDirty(false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Unable to load settings.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    void load();
  }, [section.key, scopeType, scopeId]);

  const groups = useMemo(() => {
    const visible = section.fields.filter((field) => showAdvanced || !field.advanced);
    const map = new Map<string, SettingsFieldDefinition[]>();
    for (const field of visible) {
      const group = ar ? field.groupAr : field.groupEn;
      map.set(group, [...(map.get(group) ?? []), field]);
    }
    return [...map.entries()];
  }, [section.fields, showAdvanced, ar]);

  const save = async (): Promise<SettingsSectionState | null> => {
    setBusy(true);
    setError("");
    try {
      const saved = await saveSettingsDraft(
        section.key,
        scopeType,
        scopeId,
        values,
        state?.draftRevision?.version
      );
      setState(saved);
      setValues(structuredClone(saved.draftValues ?? saved.publishedValues));
      setDirty(false);
      toast.notify({
        tone: "success",
        title: ar ? "تم حفظ المسودة" : "Draft saved",
        message: ar
          ? "لن تؤثر التغييرات في النظام قبل نشرها."
          : "Changes do not affect the product until they are published."
      });
      onStateChanged?.();
      return saved;
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Unable to save draft.");
      return null;
    } finally {
      setBusy(false);
    }
  };

  const openReview = async () => {
    let current = state;
    if (dirty || !state?.draftRevision) {
      current = await save();
    }
    if (current?.draftRevision) {
      setReviewOpen(true);
    }
  };

  const publish = async () => {
    if (!state?.draftRevision) return;
    setBusy(true);
    setError("");
    try {
      const published = await publishSettings(
        section.key,
        scopeType,
        scopeId,
        state.draftRevision.version
      );
      setState(published);
      setValues(structuredClone(published.publishedValues));
      setDirty(false);
      setReviewOpen(false);
      toast.notify({
        tone: "success",
        title: ar ? "تم نشر الإعدادات" : "Settings published",
        message: ar
          ? "أصبحت القيم الفعالة متاحة للوحدات المتأثرة."
          : "Effective values are now available to impacted modules."
      });
      onStateChanged?.();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Unable to publish settings.");
    } finally {
      setBusy(false);
    }
  };

  const discard = async () => {
    setBusy(true);
    setError("");
    try {
      await discardSettingsDraft(section.key, scopeType, scopeId);
      toast.notify({
        tone: "info",
        title: ar ? "تم حذف المسودة" : "Draft discarded"
      });
      await load();
      onStateChanged?.();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Unable to discard draft.");
      setBusy(false);
    }
  };

  const setValue = (path: string, value: unknown) => {
    setValues((current) => setPath(current, path, value));
    setDirty(true);
  };

  const enableOverride = (field: SettingsFieldDefinition) => {
    const inherited = getPath(state?.effectiveValues, field.key) ?? field.default;
    setValues((current) => setPath(current, field.key, inherited));
    setDirty(true);
  };

  const removeOverride = (field: SettingsFieldDefinition) => {
    setValues((current) => deletePath(current, field.key));
    setDirty(true);
  };

  if (busy && !state) {
    return (
      <Card>
        <Inline>
          <Spinner label={ar ? "جارٍ التحميل" : "Loading"} />
          <span>{ar ? "جارٍ تحميل الإعدادات…" : "Loading settings…"}</span>
        </Inline>
      </Card>
    );
  }

  if (!state) {
    return (
      <Alert tone="danger" title={ar ? "تعذر فتح القسم" : "Section unavailable"}>
        {error}
      </Alert>
    );
  }

  const draftLabel = state.hasUnpublishedChanges
    ? ar
      ? "تغييرات غير منشورة"
      : "Unpublished changes"
    : ar
      ? "منشور"
      : "Published";

  return (
    <Stack>
      <Panel
        title={title}
        description={description}
        actions={
          <Inline>
            <StatusBadge tone={state.hasUnpublishedChanges ? "warning" : "success"}>
              {draftLabel}
            </StatusBadge>
            <StatusBadge tone="neutral">{state.completionPercent}%</StatusBadge>
          </Inline>
        }
      >
        <Stack>
          <div className="settings-inheritance">
            <strong>{ar ? "سلسلة الوراثة" : "Inheritance chain"}</strong>
            <Inline>
              {state.inheritanceChain.map((item) => (
                <StatusBadge key={item} tone="neutral">{item}</StatusBadge>
              ))}
            </Inline>
          </div>

          {error ? (
            <Alert tone="danger" title={ar ? "تعذر تنفيذ الإجراء" : "Action failed"}>
              {error}
            </Alert>
          ) : null}

          {groups.map(([group, fields]) => (
            <Card key={group} title={group} className="settings-field-group">
              <Grid columns={2}>
                {fields.map((field) => (
                  <SettingField
                    key={field.key}
                    field={field}
                    language={language}
                    currency={currency}
                    scopeType={scopeType}
                    values={values}
                    effectiveValues={state.effectiveValues}
                    onChange={(value) => setValue(field.key, value)}
                    onEnableOverride={() => enableOverride(field)}
                    onRemoveOverride={() => removeOverride(field)}
                  />
                ))}
              </Grid>
            </Card>
          ))}

          {section.fields.some((field) => field.advanced) ? (
            <Button
              type="button"
              variant="ghost"
              onClick={() => setShowAdvanced((value) => !value)}
            >
              {showAdvanced
                ? ar
                  ? "إخفاء الإعدادات المتقدمة"
                  : "Hide advanced settings"
                : ar
                  ? "إظهار الإعدادات المتقدمة"
                  : "Show advanced settings"}
            </Button>
          ) : null}
        </Stack>
      </Panel>

      <div className="settings-sticky-actions">
        <div>
          <strong>{dirty ? (ar ? "لديك تعديلات غير محفوظة" : "You have unsaved edits") : draftLabel}</strong>
          <small>
            {state.draftRevision
              ? `${ar ? "آخر تحديث" : "Last update"}: ${formatDate(state.draftRevision.updatedAt, language)}`
              : ar
                ? "لا توجد مسودة حاليًا."
                : "No draft currently exists."}
          </small>
        </div>
        <Inline>
          {state.hasUnpublishedChanges ? (
            <Button type="button" variant="ghost" disabled={busy} onClick={() => void discard()}>
              {ar ? "حذف المسودة" : "Discard draft"}
            </Button>
          ) : null}
          <Button type="button" variant="secondary" disabled={busy || !dirty} onClick={() => void save()}>
            {ar ? "حفظ المسودة" : "Save draft"}
          </Button>
          <Button type="button" disabled={busy || (!dirty && !state.hasUnpublishedChanges)} onClick={() => void openReview()}>
            {ar ? "مراجعة ونشر" : "Review & publish"}
          </Button>
        </Inline>
      </div>

      <Dialog
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        title={ar ? "مراجعة تأثير التغييرات" : "Review change impact"}
        description={
          ar
            ? "سيتم نشر هذه القيم على النطاق المحدد وتصبح فعالة فورًا."
            : "These values will be published to the selected scope and become effective immediately."
        }
        footer={
          <Inline>
            <Button type="button" variant="secondary" onClick={() => setReviewOpen(false)}>
              {ar ? "العودة للتعديل" : "Back to editing"}
            </Button>
            <Button type="button" disabled={busy} onClick={() => void publish()}>
              {ar ? "نشر الإعدادات" : "Publish settings"}
            </Button>
          </Inline>
        }
      >
        <Stack>
          <Alert tone="warning" title={ar ? "الوحدات المتأثرة" : "Impacted modules"}>
            {section.impacts.join(" · ")}
          </Alert>
          <div className="settings-review-json">
            <pre>{JSON.stringify(values, null, 2)}</pre>
          </div>
        </Stack>
      </Dialog>
    </Stack>
  );
}

function SettingField({
  field,
  language,
  currency,
  scopeType,
  values,
  effectiveValues,
  onChange,
  onEnableOverride,
  onRemoveOverride
}: {
  field: SettingsFieldDefinition;
  language: Language;
  currency: string;
  scopeType: SettingsScopeType;
  values: Record<string, unknown>;
  effectiveValues: Record<string, unknown>;
  onChange: (value: unknown) => void;
  onEnableOverride: () => void;
  onRemoveOverride: () => void;
}) {
  const ar = language === "ar";
  const label = ar ? field.labelAr : field.labelEn;
  const description = ar ? field.descriptionAr : field.descriptionEn;
  const organizationScope = scopeType === "Organization";
  const overridden = organizationScope || hasPath(values, field.key);
  const value = overridden
    ? getPath(values, field.key)
    : getPath(effectiveValues, field.key) ?? field.default;

  const wrapper = (control: React.ReactNode) => (
    <div className={`settings-field ${!overridden ? "is-inherited" : ""}`}>
      {!organizationScope ? (
        <div className="settings-field__scope">
          <StatusBadge tone={overridden ? "info" : "neutral"}>
            {overridden
              ? ar
                ? "تخصيص هذا النطاق"
                : "Scope override"
              : ar
                ? "موروث"
                : "Inherited"}
          </StatusBadge>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={overridden ? onRemoveOverride : onEnableOverride}
          >
            {overridden
              ? ar
                ? "استخدام القيمة الموروثة"
                : "Use inherited"
              : ar
                ? "تخصيص"
                : "Override"}
          </Button>
        </div>
      ) : null}
      <div aria-disabled={!overridden}>{control}</div>
    </div>
  );

  if (field.type === "boolean") {
    return wrapper(
      <Switch
        label={label}
        description={description || undefined}
        data-testid={`setting-${field.key}`}
        checked={Boolean(value)}
        disabled={!overridden}
        onChange={(event) => onChange(event.target.checked)}
      />
    );
  }

  if (field.type === "textarea") {
    return wrapper(
      <TextAreaField
        label={label}
        hint={description || undefined}
        data-testid={`setting-${field.key}`}
        rows={4}
        value={String(value ?? "")}
        disabled={!overridden}
        onChange={(event) => onChange(event.target.value)}
      />
    );
  }

  if (field.type === "select") {
    return wrapper(
      <SelectField
        label={label}
        hint={description || undefined}
        data-testid={`setting-${field.key}`}
        value={String(value ?? "")}
        disabled={!overridden}
        onChange={(event) => onChange(event.target.value)}
      >
        {(field.options ?? []).map((option) => (
          <option key={option.value} value={option.value}>
            {ar ? option.labelAr : option.labelEn}
          </option>
        ))}
      </SelectField>
    );
  }

  if (field.type === "multiselect") {
    const selected = Array.isArray(value) ? value.map(String) : [];
    return wrapper(
      <fieldset className="settings-multiselect" disabled={!overridden}>
        <legend>{label}</legend>
        {field.options?.map((option) => (
          <Checkbox
            key={option.value}
            label={ar ? option.labelAr : option.labelEn}
            data-testid={`setting-${field.key}-${option.value}`}
            checked={selected.includes(option.value)}
            onChange={(event) => {
              const next = event.target.checked
                ? [...selected, option.value]
                : selected.filter((item) => item !== option.value);
              onChange(next);
            }}
          />
        ))}
      </fieldset>
    );
  }

  const money = field.type === "money" || field.key.toLowerCase().endsWith("minor");
  if (money) {
    const moneyValue = value == null || value === "" ? null : Number(value);
    return wrapper(
      <MoneyField
        data-testid={`setting-${field.key}`}
        label={label}
        hint={description || undefined}
        valueMinor={moneyValue !== null && Number.isFinite(moneyValue) ? moneyValue : null}
        currency={currency}
        nullable={!field.required}
        disabled={!overridden}
        onMinorChange={onChange}
      />
    );
  }

  const numeric = ["number", "quantity"].includes(field.type);
  const inputType =
    field.type === "email"
      ? "email"
      : field.type === "url"
        ? "url"
        : field.type === "color"
          ? "color"
          : field.type === "time"
            ? "time"
            : numeric
              ? "number"
              : "text";

  return wrapper(
    <Field
      data-testid={`setting-${field.key}`}
      label={label}
      hint={description || undefined}
      required={field.required}
      type={inputType}
      value={numeric ? Number(value ?? 0) : String(value ?? "")}
      min={field.minimum}
      max={field.maximum}
      disabled={!overridden}
      onChange={(event) =>
        onChange(numeric ? Number(event.target.value) : event.target.value)
      }
    />
  );
}
